import React, { useCallback, useEffect, useState } from "react";
import SitePayUI from "./SitePay.presenterNew";
import moment from "moment";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "src/hooks/useRouter";
import { useRecoilState } from "recoil";
import {
  couponCodeAtom,
  isInitAtom,
  isShowPayAtom,
  productKeyAtom,
} from "src/atoms";
import { requestCash } from "src/service/pay";
import { useSearchParams } from "react-router-dom";
import { useProductFlags } from "src/hooks/recoil/useProductFlags";
import { IGetInicisDataRes } from "src/service/types/pay.type";
import { useIsCashApply } from "src/hooks/recoil/useIsCashApply";
import useValidationSchemas from "src/hooks/validation/useValidationSchemas";
import PriceChecker from "./PriceChecker";
import { useGetCurrencyCode } from "src/hooks/api/pay/useGetCurrencyCode";
import InicisDataChecker from "./InicisDataChecker";
import { notifySuccess } from "src/components/Common/Toast";
import { useTranslation } from "react-i18next";
import { SitePayFormContext } from "./SitePayForm.context";

const SitePayContainer = () => {
  const [isShowPay] = useRecoilState(isShowPayAtom);
  const [isInit] = useRecoilState(isInitAtom);
  const [couponCodeInfo] = useRecoilState(couponCodeAtom);
  const [productKey] = useRecoilState(productKeyAtom);
  const { isOciProduct } = useProductFlags();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type") ?? "premium";
  const [priceType, setPriceType] = useState<string>(typeParam);
  const [priceUid, setPriceUid] = useState<string>("");
  const [userPerPrice, setUserPerPrice] = useState<number>(0);
  const [userPerPriceYear, setUserPerPriceYear] = useState<number>(0);
  const [inicisData, setInicisData] = useState<IGetInicisDataRes>();
  const [planId, setPlanId] = useState<string>(""); // planId --> paypal 버튼에 사용됨
  const [isRenew] = useState<boolean>(false);
  const [startDate] = useState<string>("");
  const [selectAppList, setSelectAppList] = useState<string[]>([]);
  const [showTaxInvoiceFields, setShowTaxInvoiceFields] =
    useState<boolean>(false);
  const [taxInvoiceInfo, setTaxInvoiceInfo] = useState({
    owner: "",
    businessNumber: "",
    requestDate: moment(Date.now()),
    issuer: "",
    phoneNumber: "",
    email: "",
  });
  const { t } = useTranslation();
  const route = useRouter();

  const { requestCashSchema, requestCardSchema } = useValidationSchemas({
    type: "create",
    cloudProvider: isOciProduct ? "OCI" : "AWS",
  });
  const { currency, payCompany } = useGetCurrencyCode();
  // 현금결제 신청한 상태인지 파악 (상태에 따라 페이지 이동) — side effect
  useIsCashApply();

  // 결제수단(cash/card)에 따라 검증 스키마를 동적으로 선택
  const resolver = useCallback<Resolver<any>>(
    (values, context, options) => {
      const schema =
        values.selectPayType === "cash" ? requestCashSchema : requestCardSchema;
      return yupResolver(schema)(values, context, options);
    },
    [requestCashSchema, requestCardSchema]
  );

  const methods = useForm({
    mode: "onChange",
    resolver,
    defaultValues: {
      licenseCount: 10,
      billType: "",
      billCode: "",
      payTerm: "month",
      totalPrice: 0,
      referralId: "",
      nxacAppUids: "",
      depositor: "",
      serviceUid: "",
      payDate: "",
      upc: "",
      taxInvoice: "",
      couponCode: "",
      companyName: "",
      selectPayType: "card",
      confirmPolicy: false,
      priceType,
      priceUid,
    },
  });

  // 요금제(?type) 변경 시 로컬 상태와 폼 값을 함께 동기화
  useEffect(() => {
    setPriceType(typeParam);
    methods.setValue("priceType", typeParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeParam]);

  const handleChangeTaxInvoice = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaxInvoiceInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleChangeTaxRequestDate = (date: Date | null) => {
    setTaxInvoiceInfo((prev) => ({ ...prev, requestDate: moment(date) }));
  };

  // 폼 제출: 현금결제만 요청 처리(카드결제는 CardPayment가 자체 처리)
  const onValid = (values: any) => {
    const { licenseCount, payTerm, referralId, depositor, serviceUid, payDate, priceType: valuePriceType, totalPrice, selectPayType } = values;
    if (selectPayType !== "cash") return;
    requestCash({
      billCode: priceUid,
      billType: valuePriceType,
      depositor,
      licenseCount,
      nxacAppUids: [],
      payDate,
      payTerm,
      referralId,
      serviceUid,
      taxInvoice: showTaxInvoiceFields ? taxInvoiceInfo : {},
      totalPrice,
      ...(couponCodeInfo
        ? { product: couponCodeInfo.product, couponCode: couponCodeInfo.couponCode }
        : { product: productKey }),
    }).then(() => {
      notifySuccess(t("cashStatus.requestNotic"));
      route.goConsole();
    });
  };
  const submit = methods.handleSubmit(onValid);

  return (
    <FormProvider {...methods}>
      <SitePayFormContext.Provider value={{ submit }}>
        <SitePayUI
          userPerPrice={userPerPrice}
          userPerPriceYear={userPerPriceYear}
          currency={currency}
          payCompany={payCompany}
          isInit={isInit}
          isShowPay={isShowPay}
          planId={planId}
          isRenew={isRenew}
          startDate={startDate}
          inicisData={inicisData}
          selectAppList={selectAppList}
          setSelectAppList={setSelectAppList}
          showTaxInvoiceFields={showTaxInvoiceFields}
          toggleTaxInvoidFields={() => setShowTaxInvoiceFields((p) => !p)}
          taxInvoiceInfo={taxInvoiceInfo}
          handleChangeTaxInvoice={handleChangeTaxInvoice}
          handleChangeTaxRequestDate={handleChangeTaxRequestDate}
        />
        <PriceChecker
          currency={currency}
          setPriceType={setPriceType}
          setUserPerPrice={setUserPerPrice}
          setUserPerPriceYear={setUserPerPriceYear}
          setPlanId={setPlanId}
          setPriceUid={setPriceUid}
        />
        <InicisDataChecker
          setInicisData={setInicisData}
          priceType={priceType}
          userPerPrice={userPerPrice}
          userPerPriceYear={userPerPriceYear}
        />
      </SitePayFormContext.Provider>
    </FormProvider>
  );
};

export default SitePayContainer;
